// src/modules/app-views/_apps/appmulti/orders/config.js
// *******************************************************************************
// IMPORTS
// *******************************************************************************
import ListProvider from 'components/core/ListProvider';
import { API } from 'constants/Api';
import DocumentProvider from 'components/core/DocumentProvider';
import CardHeaderTitle from 'layouts/Pages/PageHeader/CardHeaderTitle';
import CardHeaderSubtitle from 'layouts/Pages/PageHeader/CardHeaderSubtitle';
import CardHeaderColumn from 'layouts/Pages/PageHeader/CardHeaderColumn';
import { createdAtItem, updatedAtItem } from 'layouts/Pages/PageHeader/CardHeaderColumn/items';
import CardHeaderIcons from 'layouts/Pages/PageHeader/CardHeaderIcons';

import OrdersInit from './pages/Init';
import OrdersDocument from './pages/Document';
import ordersInitActionButton from './pages/Init/actions/actionButton';
import ordersInitMenuButton from './pages/Init/actions/menuButton';
import ordersDocumentMenuButton from './pages/Document/actions/menuButton';
import ordersDocumentDropdownMenu from './pages/Document/actions/dropdownMenu';
import seedOrdersParameters from './seed';
import OrdersPreviewHeader from './pages/Init/components/OrdersPreviewHeader';
import OrdersPreviewActions from './pages/Init/components/OrdersPreviewActions';
import {
  orderBuyerEmailItem,
  orderBuyerPhoneItem,
  orderDestinyItem,
  orderSellerItem,
  orderPaymentLinkItem,
} from './pages/Document/cardHeaderItems';

// *******************************************************************************
// CONSTANTS
// *******************************************************************************
const MODULE_ID = 'orders';

// *******************************************************************************
// PRE CONFIG
// *******************************************************************************
const preConfigChanges = appId => {
  CONFIG.appId = appId;
};

// *******************************************************************************
// MODULE CONFIGURATION
// *******************************************************************************
const CONFIG = {
  version: 2,
  id: MODULE_ID,
  initialPage: 'init',
  initialParams: {},
  initialQuery: {},
  parametersModalComponent: null,
  parametersSeed: seedOrdersParameters,

  migratory: {
    title: 'orders_migration',
    schema: [],
  },
};

// *******************************************************************************
// MODULE PAGES
// *******************************************************************************
const PAGES = {
  init: {
    layout: 'list',
    moduleId: MODULE_ID,
    component: props => (
      <ListProvider {...props}>
        <OrdersInit />
      </ListProvider>
    ),

    tour: {
      firstModal: null,
      lastModal: null,
      onFinish: null,
      triggerConfettiOnFinish: false,
      videoLink: { br: null, us: null },
    },

    api: {
      url: API.APPMULTI_ORDER.PAGINATION,
      params: null,
      query: null,
      returnAsObject: false,
      autoFetch: true,
      fetchWithoutSubscription: false,
      removeSubscriptionIdFromHeader: false,
      persistInStore: true,
    },

    decorators: { request: null, response: null },

    routing: { endpoint: null, paramKey: null, query: null },

    pagination: {
      pagination: { page: 1, size: 10 },
      sort: [{ field: '_metadata.audit.updatedAt', order: 'desc', index: 1 }],
      search: {},
      textSearch: '',
      filter: {},
      drawer: { folder: 'inbox' },
    },

    table: {
      actions: ['edit', 'archive', 'remove'],
      hiddenColumns: ['createdAt'],
    },

    settings: null,

    preview: {
      actions: ['edit', 'archive', 'remove'],
      showCardHeader: false,
      noDividerAfterHeader: true,
      components: {
        title: 'order',
        header: props => <OrdersPreviewHeader {...props} />,
        body: null,
        actions: props => <OrdersPreviewActions {...props} />,
        footer: null,
      },
    },

    header: {
      visible: true,
      style: 'default',
      actions: ['filter', 'refresh', ordersInitActionButton, ordersInitMenuButton],
      navigation: {
        showTitle: true,
        showBreadcrumb: true,
        customBreadcrumb: () => ['commercial', 'orders'],
      },
    },
  },

  document: {
    highSecurity: false,

    layout: 'document',
    moduleId: MODULE_ID,
    component: props => (
      <DocumentProvider {...props}>
        <OrdersDocument />
      </DocumentProvider>
    ),

    tour: {
      firstModal: null,
      lastModal: null,
      onFinish: null,
      triggerConfettiOnFinish: false,
      videoLink: { br: null, us: null },
    },

    api: {
      url: API.APPMULTI_ORDER.URL,
      params: null,
      query: null,
      returnAsObject: true,
      autoFetch: true,
      fetchWithoutSubscription: false,
      removeSubscriptionIdFromHeader: false,
      persistInStore: true,
    },

    decorators: { request: null, response: null },

    routing: { endpoint: '/:_id', paramKey: '_id', query: null },

    pagination: null,
    table: null,

    settings: {
      initialTab: 'record',
      initialValues: {},
      actions: [
        'editRefresh',
        ordersDocumentMenuButton(),
        ordersDocumentDropdownMenu(),
        'return',
      ],
      viewMode: 'view',
    },

    header: {
      visible: true,
      style: 'card',
      avatar: { visible: true, placeholder: 'file-text' },
      navigation: {
        showTitle: true,
        showBreadcrumb: true,
        customBreadcrumb: () => ['commercial', 'orders'],
      },
      card: {
        title: props => <CardHeaderTitle {...props} placeholder="order" />,
        subtitle: props => <CardHeaderSubtitle {...props} />,
        column1: props => (
          <CardHeaderColumn
            {...props}
            items={[
              orderBuyerEmailItem,
              orderBuyerPhoneItem,
              orderDestinyItem,
              orderSellerItem,
            ]}
          />
        ),
        column2: props => (
          <CardHeaderColumn
            {...props}
            items={[
              orderPaymentLinkItem,
              createdAtItem,
              updatedAtItem,
            ]}
          />
        ),
        icons: (data, countryCode) => (
          <CardHeaderIcons data={data} countryCode={countryCode} />
        ),
      },
    },
  },
};

// *******************************************************************************
// EXPORT
// *******************************************************************************
export default { CONFIG, PAGES, preConfigChanges };
